import React from 'react';
import { FormattedMessage, FormattedDate, useIntl } from 'react-intl';
import { Loading, MessageBanner } from '@folio/stripes/components';
// SVG viewport — the chart scales to its container via width="100%".
var W = 640;
var H = 200;
var PAD_LEFT = 40;
var PAD_RIGHT = 16;
var PAD_TOP = 14;
var PAD_BOTTOM = 28;
var wrapStyle = {
    border: '1px solid #d4dbe3',
    borderRadius: '10px',
    background: '#fff',
    padding: '14px 16px',
    marginBottom: '8px',
};
/**
 * AV-03 quality trend chart (WP-D, CZNA-330): % valid over the persisted runs,
 * oldest→newest. Hand-drawn SVG — no external charting dependency (CSP-safe,
 * works in the no-node_modules worktree). Y axis is fixed 0–100 % so runs are
 * visually comparable.
 */
export var QualityTrendChart = function (_a) {
    var points = _a.points, isLoading = _a.isLoading, isError = _a.isError;
    var intl = useIntl();
    if (isLoading) {
        return React.createElement("div", { style: wrapStyle },
            React.createElement(Loading, null));
    }
    if (isError) {
        return (React.createElement(MessageBanner, { type: "error" },
            React.createElement(FormattedMessage, { id: "ui-czna-authorities.corpusQuality.trend.loadError" })));
    }
    // The trend chart only makes sense with at least two runs (AV-03 AC).
    if (!points || points.length < 2) {
        return (React.createElement("div", { style: wrapStyle, "data-test-trend-empty": true },
            React.createElement("h3", { style: { margin: '0 0 6px', fontSize: '14px', fontWeight: 700 } },
                React.createElement(FormattedMessage, { id: "ui-czna-authorities.corpusQuality.trend.title" })),
            React.createElement("p", { style: { margin: 0, fontSize: '12.5px', color: '#5d6b7a' } },
                React.createElement(FormattedMessage, { id: "ui-czna-authorities.corpusQuality.trend.needMore" }))));
    }
    var plotW = W - PAD_LEFT - PAD_RIGHT;
    var plotH = H - PAD_TOP - PAD_BOTTOM;
    var n = points.length;
    var x = function (i) { return PAD_LEFT + (n === 1 ? plotW / 2 : (i / (n - 1)) * plotW); };
    var y = function (pct) { return PAD_TOP + (1 - pct / 100) * plotH; };
    var linePath = points
        .map(function (p, i) { return "".concat(i === 0 ? 'M' : 'L', " ").concat(x(i).toFixed(1), " ").concat(y(p.validPercent).toFixed(1)); })
        .join(' ');
    var gridLines = [0, 25, 50, 75, 100];
    return (React.createElement("div", { style: wrapStyle, "data-test-trend-chart": true },
        React.createElement("h3", { style: { margin: '0 0 8px', fontSize: '14px', fontWeight: 700 } },
            React.createElement(FormattedMessage, { id: "ui-czna-authorities.corpusQuality.trend.title" })),
        React.createElement("svg", { viewBox: "0 0 ".concat(W, " ").concat(H), width: "100%", height: H, role: "img", "aria-label": intl.formatMessage({ id: 'ui-czna-authorities.corpusQuality.trend.aria' }), style: { maxWidth: '100%' } },
            gridLines.map(function (g) { return (React.createElement("g", { key: g },
                React.createElement("line", { x1: PAD_LEFT, y1: y(g), x2: W - PAD_RIGHT, y2: y(g), stroke: "#e6ecf2", strokeWidth: 1 }),
                React.createElement("text", { x: PAD_LEFT - 6, y: y(g) + 4, textAnchor: "end", fontSize: "10", fill: "#8a97a6" }, g))); }),
            React.createElement("path", { d: linePath, fill: "none", stroke: "#2c5cc5", strokeWidth: 2 }),
            points.map(function (p, i) { return (React.createElement("circle", { key: p.runId, cx: x(i), cy: y(p.validPercent), r: 3, fill: "#2c5cc5" },
                React.createElement("title", null, p.finishedAt
                    ? "".concat(intl.formatDate(p.finishedAt), ": ").concat(p.validPercent, " %")
                    : "".concat(p.validPercent, " %")))); })),
        React.createElement("div", { style: { display: 'flex', justifyContent: 'space-between', fontSize: '11px', color: '#8a97a6' } },
            React.createElement("span", null, points[0].finishedAt ? React.createElement(FormattedDate, { value: points[0].finishedAt }) : null),
            React.createElement("span", null, points[n - 1].finishedAt ? React.createElement(FormattedDate, { value: points[n - 1].finishedAt }) : null))));
};
